import { useState, ReactNode, MouseEventHandler, ElementType } from 'react';
import { cn } from '../../../utils';
import { ChevronRight, SideNavMenu } from './SecondarySideNav';

type MenuItemProps = {
    item: SideNavMenu;
    isSecondaryBarOpen: boolean;
    navigateToUrl: MouseEventHandler<HTMLAnchorElement>;
    onAccordionTriggerClicked?: (id: string) => void;
    LinkAs?: ElementType;
    open?: boolean;
    activeMenuItems?: string[];
    depth?: number;
    baseLeftPadding?: number;
};

export default function MenuItem({
    activeMenuItems = [],
    item,
    navigateToUrl,
    isSecondaryBarOpen,
    LinkAs = CreateLink,
    onAccordionTriggerClicked,
    open = false,
    depth = 0,
    baseLeftPadding = 12,
}: MenuItemProps) {
    const [isSubnavOpen, setSubnavOpen] = useState(open);
    return (
        <>
            <AccordionTrigger
                isSubnavOpen={isSubnavOpen}
                isSecondaryBarOpen={isSecondaryBarOpen}
                navigateToUrl={navigateToUrl}
                setSubnavOpen={setSubnavOpen}
                onAccordionTriggerClicked={onAccordionTriggerClicked}
                item={item}
                LinkAs={LinkAs}
                isSelected={activeMenuItems.includes(item.id)}
                depth={depth}
                baseLeftPadding={baseLeftPadding}
            />
            <AccordionContent
                activeMenuItems={activeMenuItems}
                isSubnavOpen={isSubnavOpen}
                navigateToUrl={navigateToUrl}
                item={item}
                LinkAs={LinkAs}
                onAccordionTriggerClicked={onAccordionTriggerClicked}
                isSecondaryBarOpen={isSecondaryBarOpen}
                depth={depth}
                baseLeftPadding={baseLeftPadding}
            />
        </>
    );
}

type AccordionTriggerProps = {
    isSelected: boolean;
    isSecondaryBarOpen: boolean;
    item: SideNavMenu;
    navigateToUrl: MouseEventHandler<HTMLAnchorElement>;
    isSubnavOpen: boolean;
    setSubnavOpen: (v: boolean) => void;
    onAccordionTriggerClicked?: (id: string) => void;
    LinkAs?: ElementType;
    depth: number;
    baseLeftPadding: number;
};

function AccordionTrigger({
    isSelected,
    isSecondaryBarOpen,
    isSubnavOpen,
    item,
    navigateToUrl,
    setSubnavOpen,
    onAccordionTriggerClicked,
    LinkAs = CreateLink,
    depth,
    baseLeftPadding,
}: AccordionTriggerProps) {
    const paddingLeft = `${depth * baseLeftPadding + baseLeftPadding}px`;

    const parentClass = cn(
        'alli-w-[calc(100%_-_8px)] alli-mx-auto alli-my-1 alli-flex alli-items-center alli-justify-between alli-px-3 alli-py-2 alli-text-sm alli-font-medium alli-text-nowrap alli-rounded-md alli-transition alli-duration-150',
        {
            'alli-text-transparent alli-hidden': !isSecondaryBarOpen,
            'alli-text-gray-800 hover:alli-bg-blue-gray-50 focus-visible:alli-bg-blue-gray-50':
                isSecondaryBarOpen,
            'alli-bg-blue-gray-50': isSelected,
        },
    );

    return (
        <>
            {item && item?.links && item?.links?.length > 0 ? (
                <button
                    data-testid={`accordion-trigger-${item.name}`}
                    onClick={() => {
                        onAccordionTriggerClicked &&
                            onAccordionTriggerClicked(item.id);
                        setSubnavOpen(!isSubnavOpen);
                    }}
                    className={parentClass}
                    style={{ paddingLeft }}>
                    <div
                        title={item?.name}
                        className="alli-w-full alli-overflow-hidden alli-text-ellipsis alli-whitespace-nowrap alli-text-nowrap alli-text-left">
                        {item?.name}
                    </div>
                    <ChevronRight
                        className={cn(
                            'alli-h-4 alli-w-4 alli-transform alli-text-gray-700 alli-transition-transform alli-duration-150 alli-ease-in-out',
                            {
                                'alli-rotate-0': !isSubnavOpen,
                                'alli-rotate-90': isSubnavOpen,
                            },
                        )}
                    />
                </button>
            ) : (
                <LinkAs
                    item={item}
                    onClick={navigateToUrl}
                    className={parentClass}
                    style={{ paddingLeft }}>
                    <div className="alli-w-full alli-overflow-hidden alli-text-ellipsis alli-whitespace-nowrap alli-leading-tight">
                        {item?.name}
                    </div>
                </LinkAs>
            )}
        </>
    );
}

type AccordionContentProps = {
    activeMenuItems: string[];
    item: SideNavMenu;
    isSubnavOpen: boolean;
    navigateToUrl: MouseEventHandler<HTMLAnchorElement>;
    LinkAs?: ElementType;
    onAccordionTriggerClicked?: (id: string) => void;
    isSecondaryBarOpen: boolean;
    depth: number;
    baseLeftPadding: number;
};

function AccordionContent({
    activeMenuItems,
    navigateToUrl,
    item,
    isSubnavOpen,
    LinkAs = CreateLink,
    onAccordionTriggerClicked,
    isSecondaryBarOpen,
    depth,
    baseLeftPadding,
}: AccordionContentProps) {
    if (item?.links?.length === 0 || !item?.links) return null;
    return (
        <div
            data-testid={`accordion-content-${item.name}`}
            className={cn(
                'alli-my-1 alli-flex alli-transform alli-flex-col alli-space-y-1 alli-overflow-y-scroll alli-transition-all alli-duration-300 alli-ease-in-out',
                {
                    'alli-invisible alli-max-h-0 alli-opacity-0': !isSubnavOpen,
                    'alli-visible alli-h-auto alli-max-h-screen alli-opacity-100':
                        isSubnavOpen,
                },
            )}>
            {item?.links?.map((subItem, idx) =>
                subItem?.links && subItem?.links?.length > 0 ? (
                    <MenuItem
                        key={idx}
                        item={subItem as SideNavMenu}
                        activeMenuItems={activeMenuItems}
                        navigateToUrl={navigateToUrl}
                        isSecondaryBarOpen={isSecondaryBarOpen}
                        LinkAs={LinkAs}
                        onAccordionTriggerClicked={onAccordionTriggerClicked}
                        open={isSubnavOpen}
                        depth={depth + 1}
                    />
                ) : (
                    <LinkAs
                        key={idx}
                        item={{ ...subItem, href: subItem.url }}
                        onClick={navigateToUrl}
                        className={cn(
                            'alli-mx-1 alli-flex alli-w-[calc(100%_-_8px)] alli-cursor-pointer alli-items-center alli-rounded-md alli-py-2 alli-pl-5 alli-pr-6 alli-text-gray-800 alli-transition alli-duration-150 hover:alli-bg-blue-gray-50 focus-visible:alli-bg-blue-gray-50',
                            {
                                'alli-bg-blue-gray-50':
                                    activeMenuItems.includes(subItem.id),
                            },
                        )}
                        style={{
                            paddingLeft: `${depth * baseLeftPadding + baseLeftPadding * 2}px`,
                        }}>
                        <span
                            title={subItem?.name}
                            className="alli-overflow-hidden alli-text-ellipsis alli-whitespace-nowrap alli-pr-2 alli-text-sm alli-font-medium alli-leading-tight">
                            {subItem?.name}
                        </span>
                    </LinkAs>
                ),
            )}
        </div>
    );
}

export type CreateLinkProps = {
    item: SideNavMenu;
    onClick: MouseEventHandler<HTMLAnchorElement>;
    children: ReactNode;
    className: string;
    style?: object;
};

export function CreateLink({
    item,
    onClick,
    children,
    ...props
}: CreateLinkProps) {
    const id = `alli-secondary-sidebar-${item?.id || item.name}`;
    if (item.isSpa) {
        return (
            <a
                data-testid={id}
                id={id}
                href={item.href}
                onClick={onClick}
                {...props}>
                {children}
            </a>
        );
    }

    return (
        <a data-testid={id} href={item.href} {...props}>
            {children}
        </a>
    );
}
